import type { EventqDialogPopoverPlacement } from '../dialog-popover/dialog-popover';

const DEFAULT_ANCHORED_PANEL_WIDTH_PX = 533;
const AI_FEEDBACK_PANEL_WIDTH_PX = 280;
const ANCHOR_GAP_PX = 4;
const VIEWPORT_PADDING_PX = 8;
/** Prefer flipping above when less than this remains below the anchor. */
const MIN_USABLE_BELOW_PX = 240;
/** Conservative height for flip decisions (Disarm Site/Camera is tall). */
const ESTIMATED_TOP_END_HEIGHT_PX = 560;

export interface EventqDialogPopoverPanelStyle {
  position: 'fixed';
  top: string;
  left: string;
  right: string;
  width: string;
  height?: string;
  maxHeight: string;
  transform: string;
}

export function parsePopoverPanelWidth(
  panelWidth: string | null | undefined,
  fallback = DEFAULT_ANCHORED_PANEL_WIDTH_PX,
): number {
  if (!panelWidth) {
    return fallback;
  }

  const parsed = Number.parseFloat(panelWidth);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Parses CSS length like `560px`; returns null when missing/invalid. */
export function parsePopoverPanelMaxHeightPx(
  panelMaxHeight: string | null | undefined,
): number | null {
  if (!panelMaxHeight) {
    return null;
  }

  const parsed = Number.parseFloat(panelMaxHeight);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function clampMaxHeightPx(maxHeightPx: number, maxHeightCapPx?: number | null): number {
  if (maxHeightCapPx == null || !Number.isFinite(maxHeightCapPx) || maxHeightCapPx <= 0) {
    return maxHeightPx;
  }

  return Math.min(maxHeightPx, maxHeightCapPx);
}

export function computeAnchorRightPanelStyle(
  anchorRect: DOMRect,
  panelWidth: string | null | undefined,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  maxHeightCapPx?: number | null,
): EventqDialogPopoverPanelStyle {
  const widthPx = Math.min(
    parsePopoverPanelWidth(panelWidth, AI_FEEDBACK_PANEL_WIDTH_PX),
    viewportWidth - VIEWPORT_PADDING_PX * 2,
  );
  const estimatedHeight = 220;
  let leftPx = anchorRect.right + ANCHOR_GAP_PX;
  if (leftPx + widthPx > viewportWidth - VIEWPORT_PADDING_PX) {
    leftPx = Math.max(VIEWPORT_PADDING_PX, anchorRect.left - widthPx - ANCHOR_GAP_PX);
  }
  let topPx = anchorRect.top + anchorRect.height / 2 - estimatedHeight / 2;
  topPx = Math.max(
    VIEWPORT_PADDING_PX,
    Math.min(topPx, viewportHeight - estimatedHeight - VIEWPORT_PADDING_PX),
  );
  const maxHeightPx = clampMaxHeightPx(
    Math.max(120, viewportHeight - topPx - VIEWPORT_PADDING_PX),
    maxHeightCapPx,
  );

  return {
    position: 'fixed',
    top: `${topPx}px`,
    left: `${leftPx}px`,
    right: 'auto',
    width: `${widthPx}px`,
    maxHeight: `${maxHeightPx}px`,
    transform: 'none',
  };
}

/** Top edge for `dock-left`: below a wrapping `header` when present, otherwise the anchor. */
export function resolveDockLeftTopPx(anchor: HTMLElement | null): number {
  if (!anchor) {
    return 0;
  }

  const header = anchor.closest('header');
  return (header ?? anchor).getBoundingClientRect().bottom;
}

export function computeDockLeftPanelStyle(
  panelWidth: string | null | undefined,
  viewportWidth = window.innerWidth,
  viewportHeight = window.innerHeight,
  topPx = 0,
): EventqDialogPopoverPanelStyle {
  const widthPx = Math.min(parsePopoverPanelWidth(panelWidth), viewportWidth);
  const top = Math.max(0, Math.min(topPx, viewportHeight));
  const remainingPx = Math.max(0, viewportHeight - top);

  return {
    position: 'fixed',
    top: `${top}px`,
    left: '0px',
    right: 'auto',
    width: `${widthPx}px`,
    height: `${remainingPx}px`,
    maxHeight: `${remainingPx}px`,
    transform: 'none',
  };
}

export function computeAnchoredPopoverPanelStyle(
  anchorRect: DOMRect,
  panelWidth: string | null | undefined,
  viewportWidth = window.innerWidth,
  placement: EventqDialogPopoverPlacement = 'anchor-top-end',
  viewportHeight = window.innerHeight,
  maxHeightCapPx?: number | null,
): EventqDialogPopoverPanelStyle {
  if (placement === 'dock-left') {
    return computeDockLeftPanelStyle(
      panelWidth,
      viewportWidth,
      viewportHeight,
      anchorRect.bottom,
    );
  }

  if (placement === 'anchor-right') {
    return computeAnchorRightPanelStyle(
      anchorRect,
      panelWidth,
      viewportWidth,
      viewportHeight,
      maxHeightCapPx,
    );
  }

  const widthPx = Math.min(
    parsePopoverPanelWidth(panelWidth),
    viewportWidth - VIEWPORT_PADDING_PX * 2,
  );

  const preferredBelowTop = Math.max(anchorRect.bottom + ANCHOR_GAP_PX, VIEWPORT_PADDING_PX);
  const spaceBelow = viewportHeight - preferredBelowTop - VIEWPORT_PADDING_PX;
  const spaceAbove = anchorRect.top - ANCHOR_GAP_PX - VIEWPORT_PADDING_PX;
  const shouldFlipAbove =
    spaceBelow < MIN_USABLE_BELOW_PX && spaceAbove > spaceBelow;

  let topPx: number;
  if (shouldFlipAbove) {
    const height = Math.min(
      ESTIMATED_TOP_END_HEIGHT_PX,
      Math.max(spaceAbove, MIN_USABLE_BELOW_PX),
    );
    topPx = Math.max(VIEWPORT_PADDING_PX, anchorRect.top - ANCHOR_GAP_PX - height);
  } else {
    topPx = preferredBelowTop;
  }

  let maxHeightPx = Math.max(120, viewportHeight - topPx - VIEWPORT_PADDING_PX);
  // Keep top within viewport if estimated flip overshoots.
  topPx = Math.min(topPx, Math.max(VIEWPORT_PADDING_PX, viewportHeight - maxHeightPx - VIEWPORT_PADDING_PX));
  maxHeightPx = clampMaxHeightPx(maxHeightPx, maxHeightCapPx);

  // Prefer right-right under the anchor; if that overflows left, snap left-left.
  let leftPx = anchorRect.right - widthPx;
  if (leftPx < VIEWPORT_PADDING_PX) {
    leftPx = anchorRect.left;
  }
  leftPx = Math.max(
    VIEWPORT_PADDING_PX,
    Math.min(leftPx, viewportWidth - widthPx - VIEWPORT_PADDING_PX),
  );

  return {
    position: 'fixed',
    top: `${topPx}px`,
    left: `${leftPx}px`,
    right: 'auto',
    width: `${widthPx}px`,
    maxHeight: `${maxHeightPx}px`,
    transform: 'none',
  };
}

export function shouldUseAnchoredPlacement(
  placement: EventqDialogPopoverPlacement,
  anchor: HTMLElement | null,
): boolean {
  return (
    placement === 'dock-left' ||
    ((placement === 'anchor-top-end' || placement === 'anchor-right') && anchor !== null)
  );
}
